import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Stack,
  TextField,
  Button,
  Avatar,
  Switch,
  FormControlLabel,
  FormHelperText,
  LinearProgress,
  Snackbar,
  Alert,
  Link as MuiLink,
} from '@mui/material';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import {
  salonApi,
  Salon,
  WorkingHours,
  ALL_PAYMENT_METHODS,
  enabledPaymentMethods,
} from '../../services/salonService';
import { useSalonContextStore } from '../../store/salonContextStore';
import {
  PageHeader,
  EmptyState,
  LoadingState,
  StatusPill,
  FormSection,
  FieldGrid,
  SaveBar,
  useUnsavedChangesWarning,
} from '../../components/ui';
import { tokens } from '../../theme';

interface Form {
  name: string;
  description: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  postalCode: string;
  logoUrl: string;
  coverUrl: string;
}

const EMPTY: Form = { name: '', description: '', phone: '', email: '', address: '', city: '', postalCode: '', logoUrl: '', coverUrl: '' };

interface DayHours {
  openTime: string;
  closeTime: string;
  isClosed: boolean;
}

// dayOfWeek follows JS convention (0 = Sunday). Displayed Monday-first.
const DAYS_ORDER: { dow: number; label: string }[] = [
  { dow: 1, label: 'Poniedziałek' },
  { dow: 2, label: 'Wtorek' },
  { dow: 3, label: 'Środa' },
  { dow: 4, label: 'Czwartek' },
  { dow: 5, label: 'Piątek' },
  { dow: 6, label: 'Sobota' },
  { dow: 0, label: 'Niedziela' },
];

const DEFAULT_DAY: DayHours = { openTime: '09:00', closeTime: '17:00', isClosed: true };

const COMPLETENESS_FIELDS: { key: keyof Form; label: string }[] = [
  { key: 'name', label: 'nazwa' },
  { key: 'description', label: 'opis' },
  { key: 'address', label: 'adres' },
  { key: 'city', label: 'miasto' },
  { key: 'postalCode', label: 'kod pocztowy' },
  { key: 'phone', label: 'telefon' },
  { key: 'email', label: 'e-mail' },
];

const orderedMethods = (list: string[]) =>
  ALL_PAYMENT_METHODS.map((m) => m.value as string).filter((v) => list.includes(v));

const hoursKey = (h: Record<number, DayHours>) =>
  JSON.stringify(DAYS_ORDER.map(({ dow }) => h[dow] || DEFAULT_DAY));

/** Podglad zdjecia w tle z adresu URL — pokazuje tez, gdy adres nie prowadzi do obrazka. */
const CoverPreview: React.FC<{ url: string }> = ({ url }) => {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [url]);
  const hasUrl = url.trim().length > 0;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: '100%',
        ...(hasUrl && !broken ? { aspectRatio: '4 / 1', minHeight: 96, maxHeight: 180 } : { height: 88 }),
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: tokens.surfaceStrong,
        display: 'grid',
        placeItems: 'center',
        color: tokens.subtle,
      }}
    >
      {hasUrl && !broken ? (
        <Box
          component="img"
          src={url}
          alt="Podgląd zdjęcia w tle"
          onError={() => setBroken(true)}
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <Stack alignItems="center" gap={0.5} sx={{ px: 2, textAlign: 'center' }}>
          <ImageOutlinedIcon />
          <Typography variant="caption" sx={{ color: tokens.muted }}>
            {broken ? 'Nie udało się wczytać obrazu z tego adresu' : 'Tu pojawi się podgląd zdjęcia w tle'}
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

export const SalonProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { activeSalonId, salons } = useSalonContextStore();
  const salonId = activeSalonId || salons[0]?.id || null;

  const [salon, setSalon] = useState<Salon | null>(null);
  const [activeMethods, setActiveMethods] = useState<string[]>([]);
  const [form, setForm] = useState<Form>(EMPTY);
  const [hours, setHours] = useState<Record<number, DayHours>>({});
  // Stan z ostatniego wczytania/zapisu — do wykrywania zmian osobno dla kazdej czesci.
  const [savedForm, setSavedForm] = useState<Form>(EMPTY);
  const [savedHours, setSavedHours] = useState<Record<number, DayHours>>({});
  const [savedMethods, setSavedMethods] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoBroken, setLogoBroken] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);

  useEffect(() => {
    if (!salonId) {
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const s = await salonApi.getById(salonId);
        setSalon(s);
        const methods = enabledPaymentMethods(s);
        setActiveMethods(methods);
        setSavedMethods(methods);
        const loadedForm: Form = {
          name: s.name || '',
          description: s.description || '',
          phone: s.phone || '',
          email: s.email || '',
          address: s.address || '',
          city: s.city || '',
          postalCode: s.postalCode || '',
          logoUrl: s.logoUrl || '',
          coverUrl: s.coverUrl || '',
        };
        setForm(loadedForm);
        setSavedForm(loadedForm);
        const map: Record<number, DayHours> = {};
        (s.workingHours || []).forEach((h: WorkingHours) => {
          map[h.dayOfWeek] = { openTime: h.openTime, closeTime: h.closeTime, isClosed: h.isClosed };
        });
        setHours(map);
        setSavedHours(map);
      } catch {
        setToast({ msg: 'Nie udało się załadować danych salonu. Odśwież stronę, aby spróbować ponownie.', kind: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  }, [salonId]);

  useEffect(() => setLogoBroken(false), [form.logoUrl]);
  useEffect(() => setSaveError(null), [form, hours, activeMethods]);

  const set = (k: keyof Form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const dayOf = (dow: number): DayHours => hours[dow] || DEFAULT_DAY;
  const setDay = (dow: number, patch: Partial<DayHours>) =>
    setHours((h) => ({ ...h, [dow]: { ...dayOf(dow), ...patch } }));

  const formDirty = useMemo(
    () => (Object.keys(EMPTY) as (keyof Form)[]).some((k) => form[k] !== savedForm[k]),
    [form, savedForm],
  );
  const hoursDirty = useMemo(() => hoursKey(hours) !== hoursKey(savedHours), [hours, savedHours]);
  const methodsDirty = useMemo(
    () => orderedMethods(activeMethods).join(',') !== orderedMethods(savedMethods).join(','),
    [activeMethods, savedMethods],
  );
  const dirty = formDirty || hoursDirty || methodsDirty;
  useUnsavedChangesWarning(dirty);

  const apiError = (e: unknown) =>
    (e as { response?: { data?: { message?: string; error?: string } } })?.response?.data?.message
    || (e as { response?: { data?: { error?: string } } })?.response?.data?.error;

  const save = async () => {
    if (!salonId) return;
    const updated = await salonApi.update(salonId, form);
    setSalon((prev) => ({ ...(prev as Salon), ...updated }));
    setSavedForm(form);
  };

  const saveHours = async () => {
    if (!salonId) return;
    const payload = DAYS_ORDER.map(({ dow }) => {
      const d = dayOf(dow);
      return { dayOfWeek: dow, openTime: d.openTime, closeTime: d.closeTime, isClosed: d.isClosed };
    });
    await salonApi.setWorkingHours(salonId, payload);
    setSavedHours(hours);
  };

  const savePaymentMethods = async () => {
    if (!salonId || activeMethods.length === 0) return;
    // Kolejnosc zapisujemy wedlug ALL_PAYMENT_METHODS, zeby listy na ekranach
    // nie zmienialy ukladu w zaleznosci od tego, co wlaczono jako pierwsze.
    const ordered = orderedMethods(activeMethods);
    const updated = await salonApi.update(salonId, { enabledPaymentMethods: ordered });
    setSalon((prev) => ({ ...(prev as Salon), ...updated }));
    setSavedMethods(activeMethods);
  };

  // Jeden pasek zapisu: kolejno zapisujemy tylko zmienione czesci. Jesli ktoras sie nie uda,
  // przerywamy — zapisane czesci przestaja byc „zmienione”, reszta zostaje na pasku.
  const saveAll = async () => {
    if (!salonId) return;
    const steps: { run: () => Promise<void>; fail: string }[] = [];
    if (formDirty) steps.push({ run: save, fail: 'Nie udało się zapisać danych salonu' });
    if (hoursDirty) steps.push({ run: saveHours, fail: 'Nie udało się zapisać godzin otwarcia' });
    if (methodsDirty) steps.push({ run: savePaymentMethods, fail: 'Nie udało się zapisać metod płatności' });
    setSaving(true);
    setSaveError(null);
    try {
      for (const step of steps) {
        try {
          await step.run();
        } catch (e) {
          // Blad pokazujemy w pasku zapisu, ktory zostaje otwarty — toast by go zaslanial.
          setSaveError(`${step.fail}: ${apiError(e) || 'spróbuj ponownie za chwilę'}`);
          return;
        }
      }
      setToast({ msg: 'Zmiany zapisane', kind: 'success' });
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    setSaveError(null);
    setForm(savedForm);
    setHours(savedHours);
    setActiveMethods(savedMethods);
  };

  const header = (
    <PageHeader
      breadcrumbs={[{ label: 'Ustawienia salonu' }]}
      title="Profil biznesu"
      description="Nazwa, adres, logo, godziny otwarcia i metody płatności salonu."
      actions={
        <Button variant="outlined" startIcon={<PublicOutlinedIcon />} onClick={() => navigate('/salon/website')}>
          Wizytówka WWW
        </Button>
      }
    />
  );

  if (!salonId) {
    return (
      <Box>
        {header}
        <Paper>
          <EmptyState
            icon={<StorefrontOutlinedIcon />}
            title="Nie wybrano salonu"
            description="Wybierz salon w górnym pasku, aby edytować jego dane."
          />
        </Paper>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box>
        {header}
        <Stack gap={2.5}>
          <LoadingState variant="block" height={40} />
          <LoadingState variant="block" height={420} />
        </Stack>
      </Box>
    );
  }

  const filled = COMPLETENESS_FIELDS.filter((f) => form[f.key].trim().length > 0);
  const missing = COMPLETENESS_FIELDS.filter((f) => form[f.key].trim().length === 0);
  const completeness = Math.round((filled.length / COMPLETENESS_FIELDS.length) * 100);
  const initials = (form.name || 'S').slice(0, 2).toUpperCase();
  const nameError = formDirty && !form.name.trim();
  const methodsError = activeMethods.length === 0;

  return (
    <Box>
      {header}

      <Paper sx={{ px: { xs: 2, md: 2.5 }, py: 2, mb: 2.5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} gap={{ xs: 1.25, sm: 2.5 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between" gap={1} sx={{ mb: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>Kompletność danych</Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', fontVariantNumeric: 'tabular-nums', color: completeness === 100 ? tokens.success : tokens.ink }}>
                {completeness}%
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={completeness}
              color={completeness === 100 ? 'success' : 'primary'}
              aria-label="Kompletność danych"
            />
            <Typography variant="caption" component="div" sx={{ mt: 0.75 }}>
              {missing.length === 0
                ? 'Wszystkie podstawowe dane są uzupełnione — klienci znajdą komplet informacji.'
                : `Uzupełnij: ${missing.map((m) => m.label).join(', ')}.`}
            </Typography>
          </Box>
          {salon?.slug && (
            <Box sx={{ flexShrink: 0 }}>
              <StatusPill
                label={salon.isPublicPublished ? 'Wizytówka opublikowana' : 'Wizytówka nieopublikowana'}
                tone={salon.isPublicPublished ? 'success' : 'neutral'}
              />
            </Box>
          )}
        </Stack>
      </Paper>

      <Paper sx={{ px: { xs: 2, md: 3 }, pt: { xs: 2.5, md: 3 }, pb: { xs: 0.5, md: 1 } }}>
        <FormSection
          first
          title="Informacje o salonie"
          description={
            <>
              Nazwa i krótki opis widoczne w systemie. Opis publiczny i ofertę edytujesz w{' '}
              <MuiLink component="button" type="button" onClick={() => navigate('/salon/website')} sx={{ fontSize: 'inherit', verticalAlign: 'baseline' }}>
                Wizytówce WWW
              </MuiLink>.
            </>
          }
        >
          <Stack gap={2}>
            <TextField
              label="Nazwa salonu"
              fullWidth
              value={form.name}
              onChange={set('name')}
              required
              error={nameError}
              helperText={nameError ? 'Podaj nazwę salonu — bez niej nie zapiszesz danych.' : undefined}
            />
            <TextField label="Opis" fullWidth multiline minRows={3} value={form.description} onChange={set('description')} helperText="Do 500 znaków." />
          </Stack>
        </FormSection>

        <FormSection title="Adres i kontakt" description="Pokazywane klientom w potwierdzeniach wizyt i na wizytówce.">
          <FieldGrid columns={2}>
            <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
              <TextField label="Adres (ulica i numer)" fullWidth value={form.address} onChange={set('address')} />
            </Box>
            <TextField label="Kod pocztowy" fullWidth value={form.postalCode} onChange={set('postalCode')} placeholder="00-000" helperText="W formacie 00-000" />
            <TextField label="Miasto" fullWidth value={form.city} onChange={set('city')} />
            <TextField label="Telefon" fullWidth value={form.phone} onChange={set('phone')} placeholder="+48 123 456 789" />
            <TextField label="E-mail" type="email" fullWidth value={form.email} onChange={set('email')} placeholder="kontakt@salon.pl" />
          </FieldGrid>
        </FormSection>

        <FormSection
          title="Logo i zdjęcie w tle"
          description="Wklej adres obrazka, który jest już w internecie (np. z Twojej strony lub mediów społecznościowych)."
        >
          <Stack gap={2.5}>
            <Stack direction="row" gap={2} alignItems="flex-start">
              <Avatar
                variant="rounded"
                src={form.logoUrl.trim() && !logoBroken ? form.logoUrl : undefined}
                imgProps={{ onError: () => setLogoBroken(true) }}
                sx={{ width: 72, height: 72, borderRadius: '14px', bgcolor: tokens.plumSoft, color: tokens.plum, fontSize: '1.25rem', fontWeight: 700, flexShrink: 0, mt: 3.5 }}
              >
                {initials}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  label="Adres URL logo"
                  fullWidth
                  value={form.logoUrl}
                  onChange={set('logoUrl')}
                  placeholder="https://…/logo.png"
                  helperText={
                    logoBroken && form.logoUrl.trim()
                      ? 'Pod tym adresem nie ma obrazka — sprawdź, czy link kończy się np. na .png lub .jpg.'
                      : 'Najlepiej kwadratowe logo, min. 256 × 256 px, w formacie PNG lub JPG.'
                  }
                />
              </Box>
            </Stack>
            <Box>
              <TextField
                label="Adres URL zdjęcia w tle"
                fullWidth
                value={form.coverUrl}
                onChange={set('coverUrl')}
                placeholder="https://…/zdjecie.jpg"
                helperText="Poziome zdjęcie salonu, min. 1600 px szerokości."
              />
              <Box sx={{ mt: 1.5 }}>
                <CoverPreview url={form.coverUrl} />
              </Box>
            </Box>
          </Stack>
        </FormSection>

        <FormSection
          title="Godziny otwarcia"
          description="Godziny są podstawą rezerwacji online — bez nich klienci nie wybiorą terminu."
        >
          <Box>
            {DAYS_ORDER.map(({ dow, label }, i) => {
              const d = dayOf(dow);
              return (
                <Box
                  key={dow}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'minmax(0, 1fr) auto', sm: '130px 150px minmax(0, 1fr)' },
                    alignItems: 'center',
                    columnGap: 2,
                    rowGap: 1,
                    py: 1.25,
                    borderTop: i === 0 ? 'none' : `1px solid ${tokens.line}`,
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>{label}</Typography>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Switch
                        checked={!d.isClosed}
                        onChange={(e) => setDay(dow, { isClosed: !e.target.checked })}
                        inputProps={{ 'aria-label': `${label} — otwarte` }}
                      />
                    }
                    label={
                      <Typography component="span" sx={{ fontSize: '0.875rem', color: d.isClosed ? tokens.muted : tokens.ink, minWidth: 72, display: 'inline-block' }}>
                        {d.isClosed ? 'Zamknięte' : 'Otwarte'}
                      </Typography>
                    }
                  />
                  {!d.isClosed && (
                    <Stack direction="row" gap={1} alignItems="center" sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' }, minWidth: 0 }}>
                      <TextField
                        type="time"
                        value={d.openTime}
                        onChange={(e) => setDay(dow, { openTime: e.target.value })}
                        inputProps={{ 'aria-label': `${label} — otwarcie` }}
                        sx={{ flex: 1, minWidth: 0, maxWidth: { sm: 150 } }}
                      />
                      <Typography sx={{ color: tokens.muted }}>–</Typography>
                      <TextField
                        type="time"
                        value={d.closeTime}
                        onChange={(e) => setDay(dow, { closeTime: e.target.value })}
                        inputProps={{ 'aria-label': `${label} — zamknięcie` }}
                        sx={{ flex: 1, minWidth: 0, maxWidth: { sm: 150 } }}
                      />
                    </Stack>
                  )}
                </Box>
              );
            })}
          </Box>
        </FormSection>

        <FormSection
          title="Metody płatności"
          description="Wyłączone metody znikają z kalendarza i z kasy."
        >
          <FieldGrid columns={3} sx={{ rowGap: 0.5, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' } }}>
            {ALL_PAYMENT_METHODS.map((m) => {
              const on = activeMethods.includes(m.value);
              return (
                <FormControlLabel
                  key={m.value}
                  sx={{ m: 0, py: 0.5 }}
                  control={
                    <Switch
                      checked={on}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? [...activeMethods, m.value as string]
                          : activeMethods.filter((v) => v !== m.value);
                        setActiveMethods(next);
                      }}
                    />
                  }
                  label={m.label}
                />
              );
            })}
          </FieldGrid>
          <FormHelperText error={methodsError} sx={{ mt: 1.25 }}>
            {methodsError ? 'Włącz przynajmniej jedną metodę płatności.' : 'Przynajmniej jedna metoda musi zostać włączona.'}
          </FormHelperText>
        </FormSection>
      </Paper>

      <SaveBar
        open={dirty}
        onSave={saveAll}
        onDiscard={discard}
        saving={saving}
        disabled={(formDirty && !form.name.trim()) || (methodsDirty && methodsError)}
        message={
          saveError ? saveError
          : (formDirty && !form.name.trim()) ? 'Podaj nazwę salonu, aby zapisać zmiany'
            : (methodsDirty && methodsError) ? 'Włącz przynajmniej jedną metodę płatności'
              : 'Masz niezapisane zmiany'
        }
      />

      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? (
          <Alert severity={toast.kind} onClose={() => setToast(null)} variant="filled">{toast.msg}</Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default SalonProfilePage;
